import { useState, useEffect, useMemo } from 'react';
import { resolveTrackArtUrlSync, pickCoverArtThumbHash } from '../utils/coverArt';
import { fetchValidatedLocalThumbUrl, pickRemoteTileCoverUrl } from '../utils/tileCoverArt';

/**
 * Resolve now-playing cover art: sync proxy/URL first, then Tauri cover IPC fallback.
 * @param {{ thumb?: boolean }} [options] — thumb defaults true (mini-player tiles); false for hero panels.
 */
export function useResolvedTrackArtwork(track, options) {
  const preferThumb = options?.thumb !== false;
  const syncUrl = useMemo(
    () => resolveTrackArtUrlSync(track, { thumb: preferThumb }),
    [track, preferThumb],
  );
  const [asyncUrl, setAsyncUrl] = useState(null);
  const thumbHash = pickCoverArtThumbHash(track);

  const embHash = track?.embedded_cover_hash || track?.embeddedCoverHash || null;
  const folHash = track?.folder_cover_hash || track?.folderCoverHash || null;
  const albumKey = track?.album_key || track?.albumKey || track?.albumId || null;
  const filePath = track?.file_path || null;

  useEffect(() => {
    if (syncUrl) {
      setAsyncUrl(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      if (thumbHash) {
        const validated = await fetchValidatedLocalThumbUrl(thumbHash);
        if (!cancelled && validated) {
          setAsyncUrl(validated);
          return;
        }
      }
      try {
        const { getCoverArtWithFallback, isCoverIPCAvailable, pickAlbumFolderPath } = await import('../lib/coverArtIPC');
        if (!isCoverIPCAvailable()) {
          const remote = pickRemoteTileCoverUrl(track);
          if (!cancelled && remote) setAsyncUrl(remote);
          return;
        }
        let url = null;
        if (embHash || folHash || albumKey) {
          url = await getCoverArtWithFallback(embHash, folHash, albumKey);
        }
        if (!url && filePath) {
          const folderPath = pickAlbumFolderPath({ file_path: filePath });
          if (folderPath) {
            url = await getCoverArtWithFallback(null, null, folderPath);
          }
        }
        if (!cancelled && url) {
          setAsyncUrl(url);
          return;
        }
        const remote = pickRemoteTileCoverUrl(track);
        if (!cancelled && remote) setAsyncUrl(remote);
      } catch {
        /* IPC unavailable */
      }
    })();

    return () => { cancelled = true; };
  }, [syncUrl, thumbHash, embHash, folHash, albumKey, filePath, track]);

  return syncUrl || asyncUrl || '';
}
